export type PortalActivityStatus = 'todo' | 'inProgress' | 'review' | 'done';

export type PortalActivityPriority = 'high' | 'medium' | 'low';

export type PortalActivityChecklistItem = {
  id: string;
  label: string;
  done: boolean;
};

export type PortalActivityComment = {
  id: string;
  authorName: string;
  body: string;
  whenLabel: string;
};

export type PortalActivityFile = {
  id: string;
  name: string;
  sizeLabel: string;
};

export type PortalActivityItem = {
  id: string;
  reference: string;
  title: string;
  description: string;
  status: PortalActivityStatus;
  priority: PortalActivityPriority;
  ownerName: string;
  ownerRole: string;
  stageLabel: string;
  stageId: string;
  dueLabel: string;
  startDate: string;
  endDate: string;
  progressPercent: number;
  commentCount: number;
  inBacklog: boolean;
  backlogOrder: number;
  checklist: PortalActivityChecklistItem[];
  dependencyLabel?: string;
  comments: PortalActivityComment[];
  files: PortalActivityFile[];
};

export type PortalActivitiesData = {
  projectId: string;
  progressPercent: number;
  stages: { id: string; label: string }[];
  items: PortalActivityItem[];
};

export const portalActivitiesByProjectId: Record<string, PortalActivitiesData> = {
  crm: {
    projectId: 'crm',
    progressPercent: 68,
    stages: [
      { id: 'stage-planning', label: '1. Planejamento' },
      { id: 'stage-configuration', label: '2. Configuração' },
      { id: 'stage-integration', label: '3. Integração' },
      { id: 'stage-testing', label: '4. Testes e Homologação' },
      { id: 'stage-golive', label: '5. Implantação' },
    ],
    items: [
      {
        id: 'act-modules',
        reference: 'ACT-21',
        title: 'Desenvolvimento dos módulos',
        description: 'Implementação dos módulos comerciais e de atendimento do CRM.',
        status: 'inProgress',
        priority: 'high',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Desenvolvedor',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '05/11/2026',
        startDate: '01/10/2026',
        endDate: '05/11/2026',
        progressPercent: 60,
        commentCount: 4,
        inBacklog: false,
        backlogOrder: 1,
        checklist: [
          { id: 'c1', label: 'Modelagem das entidades', done: true },
          { id: 'c2', label: 'APIs de cadastro', done: true },
          { id: 'c3', label: 'Telas de listagem', done: true },
          { id: 'c4', label: 'Regras de permissão', done: false },
          { id: 'c5', label: 'Testes unitários', done: false },
        ],
        dependencyLabel: '#12 — Configuração do ambiente',
        comments: [
          {
            id: 'cm1',
            authorName: 'Mariana Silva',
            body: 'Priorize o módulo de oportunidades para a próxima entrega.',
            whenLabel: 'há 2 horas',
          },
          {
            id: 'cm2',
            authorName: 'Rafael Oliveira',
            body: 'Listagem pronta. Seguindo com as permissões.',
            whenLabel: 'há 5 horas',
          },
          {
            id: 'cm3',
            authorName: 'Ana Souza',
            body: 'Validei o fluxo inicial com o time comercial.',
            whenLabel: 'ontem',
          },
          {
            id: 'cm4',
            authorName: 'Carlos Mendes',
            body: 'Ambiente de homologação disponível para testes.',
            whenLabel: 'ontem',
          },
        ],
        files: [
          { id: 'f1', name: 'Escopo_modulos_CRM.pdf', sizeLabel: '420 KB' },
          { id: 'f2', name: 'Wireframes_oportunidades.pdf', sizeLabel: '1.2 MB' },
          { id: 'f3', name: 'Checklist_dev.xlsx', sizeLabel: '88 KB' },
        ],
      },
      {
        id: 'act-erp',
        reference: 'ACT-22',
        title: 'Integração com ERP',
        description: 'Sincronização de clientes, pedidos e estoque com o ERP.',
        status: 'inProgress',
        priority: 'high',
        ownerName: 'Carlos Mendes',
        ownerRole: 'Desenvolvedor',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '31/10/2026',
        startDate: '11/10/2026',
        endDate: '31/10/2026',
        progressPercent: 55,
        commentCount: 2,
        inBacklog: false,
        backlogOrder: 2,
        checklist: [
          { id: 'c1', label: 'Mapeamento de campos', done: true },
          { id: 'c2', label: 'API de clientes', done: true },
          { id: 'c3', label: 'API de pedidos', done: false },
        ],
        comments: [
          {
            id: 'cm1',
            authorName: 'Carlos Mendes',
            body: 'Endpoint de clientes em homologação.',
            whenLabel: 'hoje',
          },
        ],
        files: [{ id: 'f1', name: 'Mapeamento_ERP.pdf', sizeLabel: '310 KB' }],
      },
      {
        id: 'act-tools',
        reference: 'ACT-23',
        title: 'Integração com outras ferramentas',
        description: 'Conexão com e-mail e calendário da equipe comercial.',
        status: 'inProgress',
        priority: 'medium',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Desenvolvedor',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '05/11/2026',
        startDate: '20/10/2026',
        endDate: '05/11/2026',
        progressPercent: 30,
        commentCount: 1,
        inBacklog: false,
        backlogOrder: 3,
        checklist: [{ id: 'c1', label: 'Credenciais de API', done: true }],
        comments: [],
        files: [],
      },
      {
        id: 'act-import',
        reference: 'ACT-24',
        title: 'Importação de base de clientes',
        description: 'Carga inicial da base comercial no CRM.',
        status: 'todo',
        priority: 'high',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '18/11/2026',
        startDate: '06/11/2026',
        endDate: '18/11/2026',
        progressPercent: 0,
        commentCount: 0,
        inBacklog: false,
        backlogOrder: 4,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-training',
        reference: 'ACT-25',
        title: 'Treinamento da equipe comercial',
        description: 'Sessões práticas com o time de vendas.',
        status: 'todo',
        priority: 'medium',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        stageLabel: '5. Implantação',
        stageId: 'stage-golive',
        dueLabel: '10/12/2026',
        startDate: '06/12/2026',
        endDate: '10/12/2026',
        progressPercent: 0,
        commentCount: 1,
        inBacklog: false,
        backlogOrder: 5,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-uat-prep',
        reference: 'ACT-26',
        title: 'Roteiro de homologação',
        description: 'Casos de teste e critérios de aceite para UAT.',
        status: 'todo',
        priority: 'medium',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        stageLabel: '4. Testes e Homologação',
        stageId: 'stage-testing',
        dueLabel: '20/11/2026',
        startDate: '06/11/2026',
        endDate: '20/11/2026',
        progressPercent: 0,
        commentCount: 0,
        inBacklog: false,
        backlogOrder: 6,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-permissions',
        reference: 'ACT-27',
        title: 'Matriz de permissões',
        description: 'Papéis e acessos por perfil do cliente.',
        status: 'todo',
        priority: 'low',
        ownerName: 'Carlos Mendes',
        ownerRole: 'Desenvolvedor',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '12/11/2026',
        startDate: '01/11/2026',
        endDate: '12/11/2026',
        progressPercent: 0,
        commentCount: 0,
        inBacklog: true,
        backlogOrder: 8,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-reports',
        reference: 'ACT-28',
        title: 'Relatórios comerciais',
        description: 'Dashboards de funil e conversão.',
        status: 'todo',
        priority: 'medium',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Desenvolvedor',
        stageLabel: '4. Testes e Homologação',
        stageId: 'stage-testing',
        dueLabel: '28/11/2026',
        startDate: '15/11/2026',
        endDate: '28/11/2026',
        progressPercent: 0,
        commentCount: 2,
        inBacklog: true,
        backlogOrder: 7,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-review-ui',
        reference: 'ACT-18',
        title: 'Revisão das telas de oportunidades',
        description: 'Ajustes de usabilidade apontados na validação interna.',
        status: 'review',
        priority: 'medium',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '28/10/2026',
        startDate: '20/10/2026',
        endDate: '28/10/2026',
        progressPercent: 90,
        commentCount: 3,
        inBacklog: false,
        backlogOrder: 9,
        checklist: [
          { id: 'c1', label: 'Checklist visual', done: true },
          { id: 'c2', label: 'Ajustes de copy', done: false },
        ],
        comments: [],
        files: [],
      },
      {
        id: 'act-review-api',
        reference: 'ACT-19',
        title: 'Revisão da API de contatos',
        description: 'Validação dos contratos e payloads.',
        status: 'review',
        priority: 'high',
        ownerName: 'Carlos Mendes',
        ownerRole: 'Desenvolvedor',
        stageLabel: '3. Integração',
        stageId: 'stage-integration',
        dueLabel: '29/10/2026',
        startDate: '22/10/2026',
        endDate: '29/10/2026',
        progressPercent: 85,
        commentCount: 1,
        inBacklog: false,
        backlogOrder: 10,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-kickoff',
        reference: 'ACT-01',
        title: 'Kick-off do projeto',
        description: 'Alinhamento inicial com stakeholders.',
        status: 'done',
        priority: 'high',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        stageLabel: '1. Planejamento',
        stageId: 'stage-planning',
        dueLabel: '03/09/2026',
        startDate: '01/09/2026',
        endDate: '03/09/2026',
        progressPercent: 100,
        commentCount: 2,
        inBacklog: false,
        backlogOrder: 11,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-requirements',
        reference: 'ACT-02',
        title: 'Levantamento de requisitos',
        description: 'Mapeamento de processos comerciais.',
        status: 'done',
        priority: 'high',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Consultor de negócios',
        stageLabel: '1. Planejamento',
        stageId: 'stage-planning',
        dueLabel: '10/09/2026',
        startDate: '04/09/2026',
        endDate: '10/09/2026',
        progressPercent: 100,
        commentCount: 5,
        inBacklog: false,
        backlogOrder: 12,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-solution',
        reference: 'ACT-03',
        title: 'Definição da solução',
        description: 'Arquitetura funcional do CRM.',
        status: 'done',
        priority: 'medium',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        stageLabel: '1. Planejamento',
        stageId: 'stage-planning',
        dueLabel: '15/09/2026',
        startDate: '11/09/2026',
        endDate: '15/09/2026',
        progressPercent: 100,
        commentCount: 1,
        inBacklog: false,
        backlogOrder: 13,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-env',
        reference: 'ACT-12',
        title: 'Configuração do ambiente',
        description: 'Ambientes de desenvolvimento e homologação.',
        status: 'done',
        priority: 'high',
        ownerName: 'Carlos Mendes',
        ownerRole: 'Desenvolvedor',
        stageLabel: '2. Configuração',
        stageId: 'stage-configuration',
        dueLabel: '25/09/2026',
        startDate: '16/09/2026',
        endDate: '25/09/2026',
        progressPercent: 100,
        commentCount: 0,
        inBacklog: false,
        backlogOrder: 14,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-params',
        reference: 'ACT-13',
        title: 'Parametrizações iniciais',
        description: 'Campos, pipelines e regras básicas.',
        status: 'done',
        priority: 'medium',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        stageLabel: '2. Configuração',
        stageId: 'stage-configuration',
        dueLabel: '10/10/2026',
        startDate: '26/09/2026',
        endDate: '10/10/2026',
        progressPercent: 100,
        commentCount: 2,
        inBacklog: false,
        backlogOrder: 15,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-fields',
        reference: 'ACT-14',
        title: 'Campos personalizados',
        description: 'Configuração dos campos do funil comercial.',
        status: 'done',
        priority: 'low',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        stageLabel: '2. Configuração',
        stageId: 'stage-configuration',
        dueLabel: '08/10/2026',
        startDate: '01/10/2026',
        endDate: '08/10/2026',
        progressPercent: 100,
        commentCount: 1,
        inBacklog: false,
        backlogOrder: 16,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-backlog-mobile',
        reference: 'ACT-30',
        title: 'App mobile do comercial',
        description: 'Proposta para consulta de oportunidades no celular.',
        status: 'todo',
        priority: 'low',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        stageLabel: '5. Implantação',
        stageId: 'stage-golive',
        dueLabel: '—',
        startDate: '—',
        endDate: '—',
        progressPercent: 0,
        commentCount: 0,
        inBacklog: true,
        backlogOrder: 17,
        checklist: [],
        comments: [],
        files: [],
      },
      {
        id: 'act-backlog-nps',
        reference: 'ACT-31',
        title: 'Pesquisa NPS pós go-live',
        description: 'Coleta de satisfação após a implantação.',
        status: 'todo',
        priority: 'medium',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        stageLabel: '5. Implantação',
        stageId: 'stage-golive',
        dueLabel: '—',
        startDate: '—',
        endDate: '—',
        progressPercent: 0,
        commentCount: 0,
        inBacklog: true,
        backlogOrder: 18,
        checklist: [],
        comments: [],
        files: [],
      },
    ],
  },
};
